import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TitleCardWidget from './widgets/TitleCardWidget';
import TableCard from './widgets/TableCard';
import { formatDateTimeISO } from '../utils/date';

import { useAccountInfo, useUpdatePassword, useServiceListPerUser } from '../hooks';

import styles from './MyPage.module.css';

const SERVICE_MAX_ROWS = 5;
const SERVICE_LIST_ADMIN = [
    { key: 'name', header: '서비스 이름', width: '1fr', align: 'center', search: true },
    { key: 'plan', header: '모델 월간 요금', width: '0.2fr', mobileWidth: '1fr', align: 'center' },
];
const SERVICE_LIST_USER = [{ key: 'name', header: '서비스 이름', width: '1fr', align: 'center', search: true }];

/** 비밀번호 변경 모달 */
function PasswordModal({ open, onClose, onSubmit }) {
    const [curr, setCurr] = useState('');
    const [next1, setNext1] = useState('');
    const [next2, setNext2] = useState('');
    const [err, setErr] = useState('');

    useEffect(() => {
        if (open) {
            setCurr('');
            setNext1('');
            setNext2('');
            setErr('');
        }
    }, [open]);

    const passwordRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (next1 !== next2) {
            setErr('새 비밀번호가 일치하지 않습니다.');
            return;
        }

        const pwValid = passwordRule.test(next1);
        if (!pwValid) {
            setErr('새 비밀번호는 8자 이상, 영문 대/소문자, 숫자, 특수문자를 모두 포함해야 합니다.');
            return;
        }

        setErr('');
        await onSubmit?.({ oldPassword: curr, newPassword: next1 });
    };

    if (!open) return null;
    return (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3>비밀번호 변경</h3>
                    <button className={styles.modalClose} onClick={onClose} aria-label="닫기">
                        ✕
                    </button>
                </div>
                <form className={styles.modalBody} onSubmit={handleSubmit}>
                    <label className={styles.field}>
                        <span className={styles.label}>현재 비밀번호</span>
                        <input type="password" value={curr} onChange={(e) => setCurr(e.target.value)} autoFocus />
                    </label>
                    <label className={styles.field}>
                        <span className={styles.label}>새 비밀번호</span>
                        <input type="password" value={next1} onChange={(e) => setNext1(e.target.value)} placeholder="8자 이상" />
                    </label>
                    <label className={styles.field}>
                        <span className={styles.label}>새 비밀번호 확인</span>
                        <input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} />
                    </label>
                    {err ? <p className={styles.error}>{err}</p> : null}
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btnGhost} onClick={onClose}>
                            취소
                        </button>
                        <button type="submit" className={styles.btnPrimary}>
                            변경
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function WithdrawModal({ open, onClose, onConfirm }) {
    if (!open) return null;

    return (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3>회원 탈퇴</h3>
                    <button className={styles.modalClose} onClick={onClose} aria-label="닫기">
                        ✕
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <p style={{ marginBottom: 8 }}>정말로 회원 탈퇴를 진행하시겠습니까?</p>
                    <p className={styles.error} style={{ marginBottom: 16 }}>
                        탈퇴 시 계정 정보 및 서비스 이용 이력 등이 서비스 정책에 따라 삭제되거나 복구 불가능한 상태가 될 수 있습니다.
                    </p>
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btnGhost} onClick={onClose}>
                            취소
                        </button>
                        <button type="button" className={styles.btnDanger} onClick={onConfirm}>
                            회원 탈퇴
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function formatPrice(n) {
    return (n ?? 0).toLocaleString() + '원';
}

/** 메인 컴포넌트 */
export default function MyPage() {
    const navigate = useNavigate();

    const { user, logout } = useAuth();
    const role = (user?.role || 'USER').toUpperCase();
    const isAdmin = role == 'ADMIN' || role == 'PERSONAL';
    const { data: accountInfo } = useAccountInfo(user.tenant_id, user.user_key);

    const updatePassword = useUpdatePassword();
    const [pwdOpen, setPwdOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const handleToggleMobileSearch = () => {
        setMobileSearchOpen((o) => !o);
    };

    const [serviceName, setServiceName] = useState('');
    const [searchName, setSearchName] = useState('');
    const [servicePage, setServicePage] = useState(1);
    const { data: serviceListRes, isFetching: isFetching } = useServiceListPerUser(user.tenant_id, searchName, servicePage, SERVICE_MAX_ROWS);
    const serverList = useMemo(() => {
        return (serviceListRes ?? []).map((r) => ({
            name: r.model_name,
            plan: formatPrice(r.price),
        }));
    }, [serviceListRes]);
    useMemo(() => {
        if (serviceName?.length == 0) {
            setSearchName(serviceName);
        }
    }, [serviceName]);

    const handleWithdraw = async () => {
        if (!window.confirm('정말로 회원 탈퇴를 진행하시겠습니까?')) return;

        try {
            const res = await fetch(`/api/account/withdraw`, {
                method: 'DELETE', // 실제 API 에 맞게 POST/DELETE 등 수정
                credentials: 'include',
                headers: { Accept: 'application/json' },
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => '');
                throw new Error(txt || '회원 탈퇴에 실패했습니다.');
            }

            alert('회원 탈퇴가 완료되었습니다.');
            handleLogout();
        } catch (e) {
            alert(e.message || '회원 탈퇴 처리 중 오류가 발생했습니다.');
        }
    };

    const handleLogout = (e) => {
        logout();
        navigate('/');
    };

    const handleSearchService = () => setSearchName(serviceName);
    return (
        <div className={styles.wrap}>
            <h1 className={styles.pageTitle}>계정관리</h1>

            {/* 프로필/계정 카드 */}
            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2>계정 정보</h2>
                </div>
                <div className={styles.cardBodyGrid}>
                    <div className={styles.avatar} aria-hidden="true">
                        {accountInfo?.user_name?.[0] ?? 'U'}
                    </div>

                    <div className={styles.grid2}>
                        <label className={styles.kv}>
                            <span className={styles.label}>이름</span>
                            <span className={styles.v}>{accountInfo?.user_name}</span>
                        </label>
                        <label className={styles.kv}>
                            <span className={styles.label}>이메일</span>
                            <span className={styles.v}>{accountInfo?.user_email}</span>
                        </label>
                        <label className={styles.kv}>
                            <span className={styles.label}>가입날자</span>
                            <span className={styles.v}>{formatDateTimeISO(accountInfo?.created_at)}</span>
                        </label>
                        <div className={styles.kv}>
                            <span className={styles.label}>비밀번호</span>
                            <div className={styles.inline}>
                                <input type="password" value="********" readOnly className={styles.readonly} />
                                <button type="button" className={styles.btnSecondary} onClick={() => setPwdOpen(true)}>
                                    비밀번호 변경
                                </button>
                            </div>
                        </div>
                        <div className={styles.kv}>
                            <span className={styles.label}>회원 탈퇴</span>
                            <div className={styles.inline}>
                                <button type="button" className={styles.btnDanger} onClick={() => setWithdrawOpen(true)}>
                                    회원 탈퇴
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 테넌트 카드 */}
            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2>테넌트 정보</h2>
                </div>
                <div className={styles.cardBodyCols}>
                    <div className={styles.kv}>
                        <span className={styles.label}>테넌트 구분</span>
                        <span className={styles.v}>{accountInfo?.org_type === 1 ? '개인' : '조직'}</span>
                    </div>
                    <div className={styles.kv}>
                        <span className={styles.label}>테넌트 이름</span>
                        <span className={styles.v}>{accountInfo?.org_type === 1 ? '-' : accountInfo?.tenant_name}</span>
                    </div>
                    <div className={styles.kv}>
                        <span className={styles.label}>테넌트 관리자</span>
                        <span className={styles.v}>{accountInfo?.org_type === 1 ? '-' : accountInfo?.admin_name}</span>
                    </div>
                    <div className={styles.kv}>
                        <span className={styles.label}>관리자 이메일</span>
                        <span className={styles.v}>{accountInfo?.org_type === 1 ? '-' : accountInfo?.admin_email}</span>
                    </div>
                </div>
            </section>

            {/* 서비스 카드 */}
            <section className={styles.serviceSection}>
                <TitleCardWidget
                    title="사용중인 서비스"
                    titleClassName="cardBgLightBlue cardTitleWhite"
                    headerRight={
                        <div className={styles.searchBox}>
                            {/* 데스크톱용 인라인 검색창 (모바일에서는 CSS로 숨김) */}
                            <input
                                type="search"
                                placeholder="서비스 검색"
                                value={serviceName}
                                onChange={(e) => setServiceName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchService()}
                                aria-label="서비스 검색"
                            />
                            <button
                                className={styles.iconBtn}
                                onClick={() => {
                                    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 860;
                                    if (isMobile) {
                                        setMobileSearchOpen((o) => !o);
                                    } else {
                                        handleSearchService();
                                    }
                                }}
                                aria-label="검색"
                            >
                                🔍
                            </button>
                        </div>
                    }
                >
                    <TableCard
                        data={serverList}
                        columns={isAdmin ? SERVICE_LIST_ADMIN : SERVICE_LIST_USER}
                        rows={SERVICE_MAX_ROWS}
                        pagenateSize={SERVICE_MAX_ROWS}
                        page={servicePage}
                        total={serverList?.length ?? 0}
                        onPageChange={setServicePage}
                        loading={isFetching}
                    />
                </TitleCardWidget>

                {/* 📱 모바일용 검색 오버레이 (타이틀 위에 겹치는 입력창) */}
                {mobileSearchOpen && (
                    <div className={styles.searchOverlay}>
                        <input
                            type="search"
                            placeholder="서비스 검색"
                            value={serviceName}
                            onChange={(e) => setServiceName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchService();
                                    setMobileSearchOpen(false);
                                }
                            }}
                            autoFocus
                        />
                        <button
                            className={styles.iconBtn_blu}
                            onClick={() => {
                                handleSearchService();
                                setMobileSearchOpen(false);
                            }}
                        >
                            검색
                        </button>
                    </div>
                )}
            </section>

            {/* 비밀번호 변경 모달 */}
            <PasswordModal
                open={pwdOpen}
                onClose={() => setPwdOpen(false)}
                onSubmit={async ({ oldPassword, newPassword }) => {
                    try {
                        const res = await updatePassword.mutateAsync({
                            tenant_id: user.tenant_id,
                            user_key: user.user_key,
                            oldPassword,
                            newPassword,
                        });
                        alert('비밀번호가 변경되었습니다.');
                        setPwdOpen(false);
                    } catch (e) {
                        alert(e.message);
                    }
                }}
            />
            {/* 회원 탈퇴 모달 */}
            <WithdrawModal
                open={withdrawOpen}
                onClose={() => setWithdrawOpen(false)}
                onConfirm={async () => {
                    await handleWithdraw();
                    // handleWithdraw 내부에서 성공 시 redirect 하므로 여기서 따로 close 안 해도 됨.
                    // 실패 시에는 모달을 그대로 유지하는 편이 UX 상 좋음.
                }}
            />
        </div>
    );
}
